import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Clock, Play, Timer } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { apiClient } from '../../lib/api';
import { toast } from 'sonner';

const ASSESSMENT_STORAGE_KEY = 'northern_screening_active_attempt_id';

const ACTIVITY_META = {
  perceptual_speed: { title: 'Perceptual Speed', minutes: 12, subtitle: 'Rapid visual comparison and error detection' },
  resilience: { title: 'Resilience', minutes: 8, subtitle: 'Behaviour under pressure and setbacks' },
  safety: { title: 'Safety', minutes: 8, subtitle: 'Safety judgement and procedural discipline' },
};

const toTitle = (activity) => ACTIVITY_META[activity]?.title || activity;

const formatClock = (seconds) => {
  const safe = Math.max(0, Math.floor(Number(seconds || 0)));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const prettyDimension = (value) => String(value || '').replaceAll('_', ' ');

const getBandClass = (score) => {
  if (score >= 85) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (score >= 70) return 'text-blue-700 bg-blue-50 border-blue-200';
  if (score >= 55) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
};

const MetricCell = ({ label, value }) => (
  <div className="rounded border bg-white px-3 py-2">
    <p className="text-xs text-[#64748B] uppercase tracking-wide">{label}</p>
    <p className="font-mono text-lg text-[#0B1D51] font-bold">{value}</p>
  </div>
);

const PerceptualQuestion = ({ question, onSelect, lockedOptionId }) => (
  <div className="space-y-4">
    <div className="rounded-lg border bg-slate-50 p-4">
      <p className="text-sm text-[#64748B] mb-2">{question.prompt}</p>

      {question.display?.left && question.display?.right && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded border bg-white px-3 py-2 font-mono text-sm">{question.display.left}</div>
          <div className="rounded border bg-white px-3 py-2 font-mono text-sm">{question.display.right}</div>
        </div>
      )}

      {question.display?.target && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-[#64748B]">Target pattern</p>
          <div className="rounded border bg-white px-3 py-2 font-mono text-sm">{question.display.target}</div>
        </div>
      )}

      {question.display?.reference && question.display?.candidate && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded border bg-white px-3 py-2 font-mono text-sm">{question.display.reference}</div>
          <div className="rounded border bg-white px-3 py-2 font-mono text-sm">{question.display.candidate}</div>
        </div>
      )}
    </div>

    <div className="grid sm:grid-cols-2 gap-3">
      {(question.options || []).map((opt) => (
        <Button
          key={opt.id}
          variant={lockedOptionId === opt.id ? 'default' : 'outline'}
          className="justify-start min-h-11"
          onClick={() => onSelect(opt.id)}
          disabled={Boolean(lockedOptionId)}
        >
          <span className="font-medium">{opt.label}</span>
        </Button>
      ))}
    </div>
  </div>
);

const BehaviouralQuestion = ({ question, mostLikely, leastLikely, onMostLikely, onLeastLikely, onSubmit, disabled }) => {
  const canSubmit = mostLikely && leastLikely && mostLikely !== leastLikely && !disabled;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-slate-50 p-4">
        <p className="text-sm text-[#0B1D51] leading-relaxed">{question.scenario}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <fieldset className="rounded border p-3">
          <legend className="px-1 text-sm font-semibold text-[#0B1D51]">Most likely</legend>
          <RadioGroup value={mostLikely || ''} onValueChange={onMostLikely} className="space-y-2" aria-label="Most likely response">
            {(question.options || []).map((opt) => (
              <label key={`most-${opt.id}`} className="flex items-start gap-2 rounded border p-2 cursor-pointer hover:bg-slate-50">
                <RadioGroupItem value={opt.id} id={`most-${opt.id}`} />
                <span className="text-sm text-[#0B1D51]">{opt.text}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>

        <fieldset className="rounded border p-3">
          <legend className="px-1 text-sm font-semibold text-[#0B1D51]">Least likely</legend>
          <RadioGroup value={leastLikely || ''} onValueChange={onLeastLikely} className="space-y-2" aria-label="Least likely response">
            {(question.options || []).map((opt) => (
              <label key={`least-${opt.id}`} className="flex items-start gap-2 rounded border p-2 cursor-pointer hover:bg-slate-50">
                <RadioGroupItem value={opt.id} id={`least-${opt.id}`} />
                <span className="text-sm text-[#0B1D51]">{opt.text}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      </div>

      {mostLikely && leastLikely && mostLikely === leastLikely && (
        <p className="text-sm text-red-600">Most likely and least likely must be different selections.</p>
      )}

      <Button onClick={onSubmit} disabled={!canSubmit} className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
        Submit and continue
      </Button>
    </div>
  );
};

const PracticeFeedback = ({ text, onContinue }) => (
  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
    <div>
      <p className="font-semibold text-blue-900">Practice feedback</p>
      <p className="text-sm text-blue-800 whitespace-pre-line mt-1">{text}</p>
    </div>
    <Button onClick={onContinue} className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
      Continue
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  </div>
);

const NorthernScreeningAssessment = ({ gameSessionId, onBack }) => {
  const [mode, setMode] = useState('simulation');
  const [attempt, setAttempt] = useState(null);
  const [history, setHistory] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mostLikely, setMostLikely] = useState(null);
  const [leastLikely, setLeastLikely] = useState(null);
  const [lockedOptionId, setLockedOptionId] = useState(null);
  const [practiceFeedback, setPracticeFeedback] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  const questionShownAtRef = useRef(Date.now());

  const syncHistory = useCallback(async () => {
    try {
      const res = await apiClient.getNorthernAttempts();
      setHistory(res.data || []);
    } catch (_e) {
      // History is useful but should not block an assessment attempt.
    }
  }, []);

  const loadAttempt = useCallback(async (attemptId) => {
    const res = await apiClient.getNorthernAttempt(attemptId);
    setAttempt(res.data);
    if (res.data?.mode) setMode(res.data.mode);
    if (res.data?.results) setResults(res.data.results);
    return res.data;
  }, []);

  useEffect(() => {
    const init = async () => {
      await syncHistory();
      const stored = localStorage.getItem(ASSESSMENT_STORAGE_KEY);
      if (stored) {
        try {
          await loadAttempt(stored);
        } catch (_e) {
          localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
        }
      }
      setLoading(false);
    };
    init();
  }, [loadAttempt, syncHistory]);

  const activeActivity = attempt?.current_activity;
  const activeActivityState = activeActivity ? attempt?.activities?.[activeActivity] : null;
  const currentQuestion = activeActivityState?.current_question || null;

  // The server's end_at remains authoritative. This state exists only to make the
  // countdown visibly tick; expiry is reconciled by reloading the server attempt.
  useEffect(() => {
    const endAt = activeActivityState?.end_at;
    if (!attempt?.id || attempt?.finalized || !endAt) {
      setRemainingSeconds(null);
      return undefined;
    }

    const calculate = () => Math.max(0, Math.ceil((new Date(endAt).getTime() - Date.now()) / 1000));
    setRemainingSeconds(calculate());

    const timer = setInterval(async () => {
      const remaining = calculate();
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        setPracticeFeedback(null);
        try {
          await loadAttempt(attempt.id);
        } catch (_e) {
          // A manual refresh remains available if one expiry reconciliation request fails.
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [attempt?.id, attempt?.finalized, activeActivityState?.end_at, loadAttempt]);

  const timerWarning = useMemo(() => {
    if (remainingSeconds === null) return null;
    if (remainingSeconds <= 15) return '15 seconds remaining. Wrap up now.';
    if (remainingSeconds <= 60) return 'One minute remaining.';
    return null;
  }, [remainingSeconds]);

  const activityQuestionPosition = useMemo(() => {
    if (!activeActivityState) return null;
    return {
      index: Math.min((activeActivityState.current_index || 0) + 1, activeActivityState.question_count || 0),
      total: activeActivityState.question_count || 0,
    };
  }, [activeActivityState]);

  useEffect(() => {
    setMostLikely(null);
    setLeastLikely(null);
    setLockedOptionId(null);
    if (!practiceFeedback) questionShownAtRef.current = Date.now();
  }, [currentQuestion?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const beginNewAssessment = async () => {
    setSubmitting(true);
    setPracticeFeedback(null);
    try {
      const res = await apiClient.startNorthernAssessment(mode, gameSessionId || null);
      const created = res.data;
      setAttempt(created);
      setMode(created.mode || mode);
      setResults(null);
      localStorage.setItem(ASSESSMENT_STORAGE_KEY, created.id);
      await syncHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const beginActivity = async (activity) => {
    if (!attempt?.id) return;
    setSubmitting(true);
    setPracticeFeedback(null);
    try {
      const res = await apiClient.beginNorthernActivity(attempt.id, activity);
      setAttempt(res.data);
      questionShownAtRef.current = Date.now();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not begin activity');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPerceptual = async (selectedOptionId) => {
    if (!attempt?.id || !currentQuestion?.id || submitting || lockedOptionId) return;
    setLockedOptionId(selectedOptionId);
    setSubmitting(true);
    const responseTimeMs = Date.now() - questionShownAtRef.current;
    try {
      const res = await apiClient.answerNorthernPerceptual(attempt.id, {
        activity: 'perceptual_speed',
        question_id: currentQuestion.id,
        selected_option_id: selectedOptionId,
        response_time_ms: responseTimeMs,
      });
      setAttempt(res.data.attempt);
      if (mode === 'practice' && res.data.feedback) setPracticeFeedback(res.data.feedback);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit answer');
    } finally {
      setSubmitting(false);
      setLockedOptionId(null);
    }
  };

  const submitBehavioural = async () => {
    if (!attempt?.id || !currentQuestion?.id || !activeActivity || submitting) return;
    setSubmitting(true);
    const responseTimeMs = Date.now() - questionShownAtRef.current;
    try {
      const res = await apiClient.answerNorthernBehavioural(attempt.id, {
        activity: activeActivity,
        question_id: currentQuestion.id,
        most_likely_option_id: mostLikely,
        least_likely_option_id: leastLikely,
        response_time_ms: responseTimeMs,
      });
      setAttempt(res.data.attempt);
      if (mode === 'practice' && res.data.feedback) setPracticeFeedback(res.data.feedback);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const continuePractice = () => {
    setPracticeFeedback(null);
    questionShownAtRef.current = Date.now();
  };

  const finalize = useCallback(async () => {
    if (!attempt?.id) return;
    setSubmitting(true);
    try {
      const res = await apiClient.finalizeNorthernAttempt(attempt.id);
      setAttempt(res.data.attempt);
      setResults(res.data.results);
      localStorage.removeItem(ASSESSMENT_STORAGE_KEY);
      await syncHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not finalize attempt');
    } finally {
      setSubmitting(false);
    }
  }, [attempt?.id, syncHistory]);

  useEffect(() => {
    if (!attempt || results || attempt.finalized) return;
    const allDone = ['perceptual_speed', 'resilience', 'safety'].every(
      (name) => attempt.activities?.[name]?.status === 'completed'
    );
    if (allDone) finalize();
  }, [attempt, results, finalize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[450px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
      </div>
    );
  }

  if (results) {
    const overall = Number(results.overall_practice_score || 0);
    const perceptual = results.perceptual_speed || {};
    const resilience = results.resilience || {};
    const safety = results.safety || {};
    const isPractice = (attempt?.mode || mode) === 'practice';

    return (
      <div className="space-y-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-[#0B1D51]">Northern style Maki test Results</CardTitle>
            <p className="text-sm text-[#64748B]">
              Assessment version {results.assessment_version || attempt?.assessment_version || 'current'} · Practice indicators only.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`rounded-lg border p-3 ${getBandClass(overall)}`}>
              <p className="text-sm font-medium">Overall practice score: {overall.toFixed(1)}</p>
              <p className="text-sm">{results.overall_band}</p>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
              <MetricCell label="Overall" value={overall.toFixed(1)} />
              <MetricCell label="Perceptual Speed" value={Number(perceptual.score || 0).toFixed(1)} />
              <MetricCell label="Resilience" value={Number(resilience.score || 0).toFixed(1)} />
              <MetricCell label="Safety" value={Number(safety.score || 0).toFixed(1)} />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="rounded border p-3 bg-white">
                <p className="text-xs uppercase tracking-wide text-[#64748B]">Behavioural consistency</p>
                <p className="text-sm font-semibold text-[#0B1D51] mt-1">{results.consistency_indicator?.message}</p>
              </div>
              <div className="rounded border p-3 bg-white">
                <p className="text-xs uppercase tracking-wide text-[#64748B]">Total response time</p>
                <p className="font-mono text-lg font-bold text-[#0B1D51]">{Number(results.total_time_used_seconds || 0).toFixed(0)}s</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-[#0B1D51]">Perceptual Speed Analysis</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCell label="Correct" value={perceptual.correct_answers || 0} />
            <MetricCell label="Incorrect" value={perceptual.incorrect_answers || 0} />
            <MetricCell label="Unanswered" value={perceptual.unanswered_questions || 0} />
            <MetricCell label="Accuracy" value={`${Number(perceptual.accuracy_percentage || 0).toFixed(1)}%`} />
            <MetricCell label="Completion" value={`${Number(perceptual.completion_percentage || 0).toFixed(1)}%`} />
            <MetricCell label="Average RT" value={`${Number(perceptual.average_response_time_ms || 0)}ms`} />
            <MetricCell label="Median RT" value={`${Number(perceptual.median_response_time_ms || 0)}ms`} />
            <MetricCell label="Speed + Accuracy" value={Number(perceptual.speed_accuracy_score || 0).toFixed(1)} />
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-[#0B1D51]">Resilience Dimensions</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              {Object.entries(resilience.dimension_scores || {}).map(([key, value]) => (
                <MetricCell key={key} label={prettyDimension(key)} value={`${Number(value).toFixed(1)}%`} />
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-[#0B1D51]">Safety Dimensions</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              {Object.entries(safety.dimension_scores || {}).map(([key, value]) => (
                <MetricCell key={key} label={prettyDimension(key)} value={`${Number(value).toFixed(1)}%`} />
              ))}
            </CardContent>
          </Card>
        </div>

        {isPractice ? (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-[#0B1D51]">Practice Question Review</CardTitle>
              <p className="text-sm text-[#64748B]">Detailed answer guidance is available in Practice mode for learning and familiarisation.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {[...(resilience.review || []), ...(safety.review || [])].map((row, idx) => (
                <div key={`${row.question_id}-${idx}`} className="rounded border p-3 bg-white">
                  <p className="text-sm font-medium text-[#0B1D51]">{row.scenario}</p>
                  <p className="text-xs text-[#64748B] mt-2">Your most likely: {row.your_most_likely}</p>
                  <p className="text-xs text-[#64748B]">Your least likely: {row.your_least_likely}</p>
                  <Separator className="my-2" />
                  <p className="text-xs text-[#0B1D51]">Preferred most likely: {row.preferred_most_likely}</p>
                  <p className="text-xs text-[#0B1D51]">Preferred least likely: {row.preferred_least_likely}</p>
                  <p className="text-xs text-[#64748B] mt-2">{row.explanation}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="pt-5 text-sm text-[#64748B]">
              Simulation mode does not reveal the behavioural answer key. This protects the value of repeat simulations and cohort percentile benchmarking. Use Practice mode for answer-level coaching.
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap gap-3">
          <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90" onClick={beginNewAssessment} disabled={submitting}>
            Try another {isPractice ? 'practice' : 'simulation'}
          </Button>
          <Button variant="outline" onClick={onBack}>Back to test details</Button>
        </div>
      </div>
    );
  }

  if (!attempt) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-[#0B1D51]">Northern style Maki test</CardTitle>
          <p className="text-sm text-[#64748B]">Perceptual Speed, Resilience and Safety</p>
          <p className="text-sm text-[#64748B] mt-2">A realistic independent practice assessment inspired by online train driver recruitment screening.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            {Object.entries(ACTIVITY_META).map(([key, value]) => (
              <div key={key} className="rounded border p-3 bg-white">
                <p className="text-sm font-semibold text-[#0B1D51]">{value.title}</p>
                <p className="text-xs text-[#64748B]">{value.subtitle}</p>
                <p className="font-mono text-sm mt-2">{value.minutes} minutes</p>
              </div>
            ))}
          </div>

          <div className="space-y-1 text-sm text-[#64748B]">
            <p>Simulation: 20 Perceptual Speed, 12 Resilience and 12 Safety questions.</p>
            <p>Total assessed time: approximately 28 minutes.</p>
            <p>Timers cannot be paused in Simulation mode and unanswered items receive no credit.</p>
            <p>Practice mode is untimed and provides answer-level coaching.</p>
            <p>Behavioural option order may vary between attempts.</p>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">
              This independent practice assessment is not produced, approved or endorsed by Northern Trains, Maki People, the Rail Delivery Group, RSSB or an assessment provider. Recruitment processes and assessment formats may change.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Label htmlFor="mode-select" className="text-sm text-[#0B1D51]">Mode:</Label>
              <select id="mode-select" className="border rounded px-2 py-1 text-sm" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="simulation">Simulation (timed / benchmarked)</option>
                <option value="practice">Practice (untimed / coached)</option>
              </select>
            </div>
            <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90" onClick={beginNewAssessment} disabled={submitting}>
              <Play className="w-4 h-4 mr-2" />
              Begin assessment
            </Button>
          </div>

          {history.length > 0 && (
            <div className="rounded border bg-slate-50 p-3">
              <p className="text-sm font-semibold text-[#0B1D51] mb-2">Previous attempts</p>
              <div className="space-y-1 text-xs text-[#64748B]">
                {history.slice(0, 5).map((item) => (
                  <p key={item.attempt_id}>
                    {new Date(item.started_at).toLocaleString('en-GB')}: {item.overall_practice_score == null ? 'Incomplete' : `Overall ${Number(item.overall_practice_score).toFixed(1)}`} · {item.mode} · {item.assessment_version || 'legacy'}
                  </p>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!activeActivityState) {
    return <Card><CardContent className="pt-6 text-sm text-[#64748B]">Loading activity state...</CardContent></Card>;
  }

  const status = activeActivityState.status;
  const title = toTitle(activeActivity);

  if (status !== 'in_progress') {
    return (
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-[#0B1D51]">{status === 'completed' ? 'Activity complete' : `${title} ready`}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#64748B]">
            {status === 'completed' ? 'Move to the next activity when you are ready.' : `Press Begin to start ${title}. The timer starts immediately in Simulation mode.`}
          </p>
          <Button onClick={() => beginActivity(activeActivity)} disabled={submitting || status === 'completed'} className="bg-[#0B1D51] hover:bg-[#0B1D51]/90">
            <Play className="w-4 h-4 mr-2" />
            Begin {title}
          </Button>
          <div className="rounded border bg-slate-50 p-3 text-xs text-[#64748B]">
            <p>Activity order is fixed: Perceptual Speed → Resilience → Safety.</p>
            <p>You cannot return to previous questions once submitted.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-[#0B1D51]">{title}</CardTitle>
              <p className="text-xs text-[#64748B] mt-1">Question {activityQuestionPosition?.index || 1} of {activityQuestionPosition?.total || 0}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[#0B1D51] border-[#0B1D51]/30">
                <Clock className="w-3 h-3 mr-1" />
                {attempt.mode}
              </Badge>
              {remainingSeconds !== null && (
                <Badge className={`${remainingSeconds <= 15 ? 'bg-red-600 text-white' : remainingSeconds <= 60 ? 'bg-amber-500 text-white' : 'bg-[#0B1D51] text-white'}`}>
                  <Timer className="w-3 h-3 mr-1" />
                  {formatClock(remainingSeconds)}
                </Badge>
              )}
            </div>
          </div>

          {timerWarning && (
            <div role="status" aria-live="polite" className={`mt-2 rounded px-3 py-2 text-sm ${remainingSeconds <= 15 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              {timerWarning}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {practiceFeedback ? (
            <PracticeFeedback text={practiceFeedback} onContinue={continuePractice} />
          ) : (
            <>
              {currentQuestion && activeActivity === 'perceptual_speed' && (
                <PerceptualQuestion question={currentQuestion} onSelect={submitPerceptual} lockedOptionId={lockedOptionId} />
              )}

              {currentQuestion && (activeActivity === 'resilience' || activeActivity === 'safety') && (
                <BehaviouralQuestion
                  question={currentQuestion}
                  mostLikely={mostLikely}
                  leastLikely={leastLikely}
                  onMostLikely={setMostLikely}
                  onLeastLikely={setLeastLikely}
                  onSubmit={submitBehavioural}
                  disabled={submitting}
                />
              )}

              {!currentQuestion && (
                <div className="rounded border bg-slate-50 p-4 text-sm text-[#64748B]">This activity is now complete. Prepare for the next stage.</div>
              )}
            </>
          )}

          <div className="mt-6 rounded border bg-slate-50 p-3 text-xs text-[#64748B]">
            This independent practice assessment is not produced, approved or endorsed by Northern Trains, Maki People, the Rail Delivery Group, RSSB or an assessment provider.
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Exit assessment</Button>
        <Button variant="ghost" onClick={() => loadAttempt(attempt.id)}>
          Refresh state
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </motion.div>
  );
};

export default NorthernScreeningAssessment;
